import axios from 'axios';
import config from 'config';
import { setupRequestInterceptors } from './interceptors';

const instance = axios.create({ baseURL: `${config.API_BASE_URL}/families` });
const request = setupRequestInterceptors(instance);

export const FAMILY_FILE_TYPES = {
  // optional files
  APPLICATION: { title: 'Application summary', code: 'APPLICATION', required: false },
  ORDERS: { title: 'Sponsor employment verification', code: 'ORDERS', required: false },
  SPONSOR_LES: { title: 'Sponsor income verification', code: 'SPONSOR_LES', required: false },
  SPONSOR_OTHER: { title: 'Other sponsor documents', code: 'SPONSOR_OTHER', required: false },
  SPONSOR_PAF: { title: 'Civilian personnel action form', code: 'SPONSOR_PAF', required: false },
  SPOUSE_LOOKING_FOR_WORK: { title: 'Spouse looking for work', code: 'SPOUSE_LOOKING_FOR_WORK', required: false },
  SPOUSE_SCHOOL: { title: 'Spouse schooling schedule', code: 'SPOUSE_SCHOOL', required: false },
  SPOUSE_PAYSTUB: { title: 'Spouse paystub', code: 'SPOUSE_PAYSTUB', required: false },
  SPOUSE_WORK: { title: 'Spouse employment verification', code: 'SPOUSE_WORK', required: false },
  SPOUSE_OTHER: { title: 'Other spouse documents', code: 'SPOUSE_OTHER', required: false },
  CHILD_CYS: { title: 'Child & Youth Services (CYS) verification', code: 'CHILD_CYS', required: false },
  SELF_CERTIFICATION: { title: "Children's birth verification", code: 'SELF_CERTIFICATION', required: false },
  PROVIDER_RATES: { title: "Children's rates verification", code: 'PROVIDER_RATES', required: false },
  SCHEDULE: { title: 'Schedule of care form', code: 'SCHEDULE', required: false },
  CHILD_CUSTODY: { title: 'Child custody documentation', code: 'CHILD_CUSTODY', required: false },
  CHILD_OTHER: { title: 'Other child documents', code: 'CHILD_OTHER', required: false },
  EXCEPTION_REQUEST: { title: 'Special Circumstance Documents', code: 'EXCEPTION_REQUEST', required: false },
};

const api = {
  getHouseHold: async (id) => {
    if (id) {
      const { data } = await request.get(`/households/${id}`);
      return data;
    }

    const { data } = await request.get(`/households`);
    return data;
  },
  updateHousehold: async (id, params) => {
    const { data } = await request.put(`/households/${id}`, params);
    return data;
  },
  getHouseHoldApplications: async (id) => {
    const { data } = await request.get(`/households/${id}/applications`);
    return data;
  },
  getChildren: async (id) => {
    const { data } = await request.get(`/households/${id}/children`);
    return data;
  },
  getAdults: async (id) => {
    const { data } = await request.get(`/households/${id}/adults`);
    return data;
  },
  getApplication: async (id) => {
    if (id) {
      const { data } = await request.get(`/applications/${id}`);
      return data;
    } else {
      const { data } = await request.get(`/applications`);
      if (data.length === 0) return null;
      const { data: application } = await request.get(`/applications/${data.at(-1)?.id}`);
      return application;
    }
  },
  getApplicationsByUserId: async (userId) => {
    const { data } = await request.get(`/applications/persons/${userId}/list`);
    return data;
  },
  saveApplication: async (payload) => {
    if (payload.id) {
      const { data } = await request.put(`/applications/${payload.id}`, payload);
      return data;
    } else {
      const { data } = await request.post(`/applications`, payload);
      return data;
    }
  },
  addApplicant: async (id, payload) => {
    const { data } = await request.post(`/applications/${id}/applicant`, payload);
    return data;
  },
  submitApplication: async (id) => {
    const { data } = await request.put(`/applications/${id}/submit`);
    return data;
  },
  acceptAgreement: async (id, payload) => {
    const { data } = await request.post(`/applications/${id}/agreement/accept`, payload);
    return data;
  },
  acceptSelfCert: async (appId, payload) => {
    const { data } = await request.post(`/applications/${appId}/self-cert/accept`, payload);
    return data;
  },
  addSecondParent: async (appId, payload) => {
    const { data } = await request.post(`/applications/${appId}/parents`, payload);
    return data;
  },
  saveChild: async (id, payload) => {
    const { data } = await request.post(`/applications/${id}/children`, payload);
    return data;
  },
  assignCoordinator: async (payload) => {
    if (!payload) return;
    const response = await request.post('/applications/assign', payload);
    const { data } = response;
    return data;
  },
  assignReviewer: async (payload) => {
    const response = await request.post('/applications/reviewer/assign', payload);
    const { data } = response;
    return data;
  },
  getOptions: async (url) => {
    const response = await request.get(url);
    const { data } = response;
    return data;
  },
  getParentEducations: async (parentId) => {
    const { data } = await request.get(`/persons/${parentId}/education`);
    return data;
  },
  addUpdateParentEducations: async (parentId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/persons/${parentId}/education${payload.id ? `/${payload.id}` : ''}`,
      payload.id ? payload : [payload],
    );
    return data;
  },
  deleteParentEducation: async (parentId, id) => {
    const { data } = await request.delete(`/persons/${parentId}/education/${id}`);
    return data;
  },
  getApplicationEligibility: async (appId) => {
    const { data } = await request.get(`/applications/${appId}/eligibility`);
    return data;
  },
  getSchoolInfo: async (personId) => {
    const { data } = await request.get(`/persons/${personId}/education`);
    return data;
  },
  addUpdateSchool: async (personId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/persons/${personId}/education${payload.id ? `/${payload.id}` : ''}`,
      payload,
    );
    return data;
  },
  addUpdateAdult: async (id, adultId, payload, self) => {
    const isUpdate = Boolean(adultId);
    const adultUrl = isUpdate
      ? `/households/${id}/adults/${adultId}`
      : `/households/${id}/adults?self=${self === true}`;
    const { data } = await request[isUpdate ? 'put' : 'post'](adultUrl, payload);
    return data;
  },
  addUpdateSchoolInfo: async (personId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/persons/${personId}/education${payload.id ? `/${payload.id}` : ''}`,
      payload,
    );
    return data;
  },
  deleteSchoolInfo: async (personId, educationId, payload) => {
    const { data } = await request[payload.id ? 'put' : 'post'](
      `/persons/${personId}/education/${educationId}`,
      payload,
    );
    return data;
  },
  addUpdateFamilyChild: async (id, childId, payload) => {
    const { data } = await request[childId ? 'put' : 'post'](
      `/households/${id}/children${childId ? `/${childId}` : ''}`,
      payload,
    );
    return data;
  },
  getNotes: async (id, params) => {
    const { data } = await request.get(`/applications/${id}/notes`, { params });
    return data;
  },
  /*
   * APPLICATION FILES
   */
  getApplicationFiles: async (appId) => {
    const { data } = await request.get(`/applications/${appId}/files`);
    return data;
  },
  getApplicationFileContents: async (appId, fileId) => {
    const { data } = await request.get(`/applications/${appId}/files/${fileId}/contents`);
    return data;
  },
  uploadApplicationFile: async (appId, file, fileType) => {
    let formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType || FAMILY_FILE_TYPES.OTHER.code);

    const { data } = await request.post(`/applications/${appId}/files`, formData);
    return data;
  },
  deleteApplicationFile: async (appId, fileId) => {
    const { data } = await request.delete(`/applications/${appId}/files/${fileId}`);
    return data;
  },
  downloadAllApplicationFiles: async (appId) => {
    const { data } = await request.post(`/applications/${appId}/files/download`, {});
    return data;
  },
  /*
   * DOCUMENTATION REQUIREMENTS
   */
  getApplicantDocumentationRequirements: async (appId, personId) => {
    const { data } = await request.get(`/applications/${appId}/applicant/${personId}/files/requirements`);
    return data;
  },
  getSpouseDocumentationRequirements: async (appId, personId) => {
    const { data } = await request.get(`/applications/${appId}/parents/${personId}/files/requirements`);
    return data;
  },
  getChildDocumentationRequirements: async (appId, childIds) => {
    const { data } = await request.get(
      `/applications/${appId}/children/files/requirements?childIds=${childIds.join(',')}`,
    );
    return data;
  },
  /*
   * CHILD SCHEDULES
   */
  getChildSchedules: async (appId, childId) => {
    const { data } = await request.get(`/applications/${appId}/children/${childId}/schedules`);
    return data;
  },
  saveChildSchedule: async (appId, childId, scheduleId, payload) => {
    const { data } = await request[scheduleId ? 'put' : 'post'](
      `/applications/${appId}/children/${childId}/schedules${scheduleId ? `/${scheduleId}` : ''}`,
      payload,
    );
    return data;
  },
  /*
   * APPLICATION PROVIDERS
   */
  getApplicationProviders: async (appId) => {
    const { data } = await request.get(`/applications/${appId}/providers`);
    return data;
  },
  createApplicationProvider: async (appId, payload) => {
    const { data } = await request.post(`/applications/${appId}/providers`, payload);
    return data;
  },
  updateApplicationProvider: async (appId, providerId, payload) => {
    const { data } = await request.put(`/applications/${appId}/providers/${providerId}`, payload);
    return data;
  },
  deleteApplicationProvider: async (appId, providerId) => {
    const { data } = await request.delete(`/applications/${appId}/providers/${providerId}`);
    return data;
  },
  /*
   * STEP COMPLETION TRACKING
   */
  getStepCompletions: async (applicationId) => {
    const { data } = await request.get(`/applications/${applicationId}/steps`);
    return data;
  },
  createStepCompletion: async (applicationId, stepsArray) => {
    const { data } = await request.post(`/applications/${applicationId}/steps`, stepsArray);
    return data;
  },
  updateStepCompletion: async (applicationId, stepId, stepKey, completed = true) => {
    const { data } = await request.put(`/applications/${applicationId}/steps/${stepId}`, {
      id: stepId,
      applicationId: parseInt(applicationId),
      stepKey,
      completed,
      completedDate: new Date().toISOString(),
    });
    return data;
  },
  getProgramDetails: async (appId) => {
    const { data } = await request.get(`/applications/${appId}/program-details`);
    return data;
  },
  acceptProgramDetails: async (appId) => {
    const { data } = await request.post(`/applications/${appId}/program-details/accept`);
    return data;
  },
};
export { request };
export default api;

/*
 * COMBINE all corresponding descriptions under the same file type
 */
export const combineDocumentationRequirements = (requirements) => {
  const combinedRequirements = [];

  requirements.requiredFiles.forEach((_requiredFile) => {
    const _existingFile = combinedRequirements.find((requirement) => requirement.fileType === _requiredFile.fileType);

    if (_existingFile) {
      if (!_existingFile.descriptions) {
        _existingFile.descriptions = [];
      }

      _existingFile.descriptions.push({
        personName: _requiredFile.personName,
        description: _requiredFile.description,
      });
    } else {
      combinedRequirements.push({
        ..._requiredFile,
        descriptions: [
          {
            personName: _requiredFile.personName,
            description: _requiredFile.description,
          },
        ],
      });
    }
  });

  return combinedRequirements;
};
